import React, { useState } from 'react';
import TopNav from './TopNav';
import Footer from './Footer';
import useSEO from '../hooks/useSEO';

function NewsEventsPage({ logo }) {
  useSEO({
    title: 'News & Events',
    description: 'Upcoming conferences, presentations, and events where Veydra is sharing simulation and decision intelligence insights.'
  });

  const [expandedEvent, setExpandedEvent] = useState(0);

  const upcomingEvents = [
    {
      date: 'May 16–19, 2026',
      title: 'IISE Annual Conference & Expo 2026',
      summary: 'Industry panel on simulation and operations research',
      description: 'Alton will represent Veydra on an industry panel discussing the intersection of AI, simulation, and industrial engineering. The IISE conference brings together 2,000+ professionals in operations research and engineering management.',
      location: 'Arlington, TX',
      type: 'Panel',
      link: 'https://www.iise.org/annual/'
    },
    {
      date: 'July 20–24, 2026',
      title: 'International System Dynamics Conference 2026',
      summary: 'Presenting at ISDC 2026',
      description: 'Presenting at the 2026 International System Dynamics Conference (ISDC), sharing simulation and system dynamics insights with the global SD community.',
      location: 'Delft, Netherlands',
      type: 'Presentation',
      link: 'https://systemdynamics.org/event/2026-international-system-dynamics-conference/'
    }
  ];

  const onRadarEvents = [
    {
      date: 'November 1–4, 2026',
      title: 'INFORMS Annual Meeting',
      location: 'San Francisco, CA',
      type: 'Conference',
      link: 'https://www.informs.org/Meetings-Conferences/INFORMS-Conference-Calendar/2026-INFORMS-Annual-Meeting'
    },
    {
      date: 'November 5–7, 2026',
      title: 'APPAM Fall Research Conference',
      location: 'Boston, MA',
      type: 'Conference',
      link: 'https://www.appam.org/conference-events/2026appam/'
    },
    {
      date: 'November 21–23, 2026',
      title: 'Decision Sciences Institute Annual Conference',
      location: 'San Francisco, CA',
      type: 'Conference',
      link: 'https://decisionsciences.org/conferences/'
    },
    {
      date: 'December 6–9, 2026',
      title: 'Winter Simulation Conference',
      location: 'Glasgow, Scotland',
      type: 'Conference',
      link: 'https://meetings.informs.org/wordpress/wsc2026/'
    }
  ];

  const pastNews = [
    {
      date: 'March 19–21, 2026',
      title: 'ABSEL Annual Conference — Logan',
      description: 'Alton presented at the Association for Business Simulation and Experiential Learning (ABSEL) annual conference, sharing approaches to experiential learning and business simulation with educators and practitioners.',
      category: 'Conference',
      link: 'https://absel.org/'
    },
    {
      date: 'February 18–20, 2026',
      title: '2026 SEDSI Conference — Jacksonville',
      description: 'Alton presented in the Data, Analytics, and Statistics Instruction (DASI) track on "Interactive Simulations and Cutting-edge Innovations" — exploring how educators can bring system dynamics into the classroom.',
      category: 'Conference',
      link: 'https://sedsi.decisionsciences.org/conference/'
    },
    {
      date: 'February 4–7, 2026',
      title: 'Silicon Slopes Summit 2026 — Salt Lake City',
      description: 'Veydra was selected for the Startup Founders Showcase at Utah\'s largest tech conference, demonstrating how simulation-driven decision intelligence is transforming strategic planning.',
      category: 'Conference',
      link: 'https://www.siliconslopes.com/c/events/summit-2026-innovation-doesn-t-slow-down'
    },
    {
      date: 'December 7, 2025',
      title: 'Winter Simulation Conference — Seattle',
      description: 'Alton presented a hands-on tutorial "AI-enabled Simulation: Building Smart Simulations Faster" — walking through a modern simulation workflow from problem framing to automated calibration and policy evaluation using the Veydra platform.',
      category: 'Conference',
      image: '/WSC_2025_Logo_Web-1.png',
      link: 'https://www.wintersim.org/'
    },
    {
      date: 'November 22–24, 2025',
      title: 'Decision Sciences Institute Annual Conference — Orlando',
      description: 'Alton presented "Advancing Decision Intelligence with AI-Assisted Simulation" — demonstrating how systems thinking and AI-assisted simulation accelerate decision-making through case studies in workforce scheduling, healthcare capacity, supply chains, and training pipelines using the Strategic Decision Lab.',
      category: 'Conference',
      image: '/dsi.png',
      link: 'https://decisionsciences.org/conferences/'
    },
    {
      date: 'November 13–15, 2025',
      title: 'APPAM Fall Research Conference — Seattle',
      description: 'Participated in the 47th Annual Fall Research Conference "Forging Collaborations for Transformative and Resilient Policy Solutions" — connecting with policy researchers and practitioners on simulation-driven approaches to public policy analysis.',
      category: 'Conference',
      image: '/appam.jpg',
      link: 'https://www.appam.org/'
    },
    {
      date: 'November 6, 2025',
      title: 'Penn State Water-Energy-Food Nexus Webinar',
      description: '"AI-Augmented System Thinking and Dynamics Modeling with Veydra.io" — introducing Veydra\'s integrated environment for building, simulating, and visualizing dynamic systems in the context of the Water-Energy-Food Nexus. The webinar showcased how Veydra combines scientific rigor with modern usability and LLM intelligence.',
      category: 'Webinar',
      image: '/pennstate-wef.png',
      link: 'https://agsci.psu.edu/global/wef'
    },
    {
      date: 'October 26–29, 2025',
      title: 'INFORMS Annual Meeting — Atlanta',
      description: 'Alton presented "Decision Intelligence in Action: AI-Assisted Simulation with the Strategic Decision Lab" — a hands-on workshop where participants explored interactive models for workforce scheduling, healthcare capacity, supply chain bullwhip, and training pipeline readiness using Veydra\'s AI-assisted simulation platform.',
      category: 'Conference',
      image: '/informs2025.jpg',
      link: 'https://www.informs.org/'
    },
    {
      date: 'August 4–8, 2025',
      title: 'International System Dynamics Conference — Boston',
      description: 'Presented research on AI-assisted dynamic modeling of Utah ski tourism in partnership with the Utah Governor\'s Office of Tourism — exploring how geopolitical shifts, traveler sentiment, and the Winter Olympics announcement may influence international visitation patterns and seasonal demand.',
      category: 'Conference',
      image: '/isdc_system_dynamics_society.png',
      link: 'https://systemdynamics.org/conference-proceedings/'
    }
  ];

  const getCategoryColor = (category) => {
    const colors = {
      'Product': 'bg-blue-50 text-blue-700',
      'Partnership': 'bg-green-50 text-green-700',
      'Conference': 'bg-purple-50 text-purple-700',
      'Webinar': 'bg-indigo-50 text-indigo-700',
      'Press': 'bg-yellow-50 text-yellow-700',
      'Company': 'bg-gray-100 text-gray-700',
      'Case Study': 'bg-orange-50 text-orange-700'
    };
    return colors[category] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="min-h-screen bg-white">
      <TopNav logo={logo} />

      {/* Hero */}
      <main className="max-w-5xl mx-auto px-8 py-20 md:py-28 text-center">
        <p className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-6">News & Events</p>
        <h1 className="text-4xl md:text-5xl font-medium text-gray-900 mb-6 leading-tight tracking-tight">
          Where we've been, where we're headed
        </h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
          Updates on our work, upcoming speaking engagements, and what we're thinking about in the world of simulation and decision intelligence.
        </p>
      </main>

      {/* Upcoming Events */}
      <section className="py-16 md:py-20 px-8 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-medium text-gray-900 mb-8 tracking-tight">Upcoming Events</h2>
          
          <div className="space-y-3">
            {upcomingEvents.map((event, index) => (
              <div 
                key={index}
                className={`border rounded-lg overflow-hidden transition-all bg-white ${expandedEvent === index ? 'border-gray-900 border-2' : 'border-gray-200 hover:border-gray-300'}`}
              >
                {/* Accordion Header */}
                <button
                  onClick={() => setExpandedEvent(expandedEvent === index ? -1 : index)}
                  className="w-full p-6 text-left flex items-center justify-between gap-4"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <span className="text-sm font-medium text-gray-900">{event.date}</span>
                      <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded">{event.type}</span>
                      <span className="text-sm text-gray-500 flex items-center">
                        <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        {event.location}
                      </span>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900">{event.title}</h3>
                    {expandedEvent !== index && (
                      <p className="text-sm text-gray-600 mt-1">{event.summary}</p>
                    )}
                  </div>
                  <svg 
                    className={`w-5 h-5 text-gray-400 transition-transform flex-shrink-0 ${expandedEvent === index ? 'rotate-180' : ''}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Accordion Content */}
                {expandedEvent === index && (
                  <div className="px-6 pb-6 border-t border-gray-100">
                    <p className="text-gray-600 mt-4 mb-4 leading-relaxed">{event.description}</p>
                    <a 
                      href={event.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-sm font-medium text-gray-900 hover:text-gray-700"
                    >
                      Learn more & register
                      <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-8 text-center">
            <a href="/contact">
              <button className="border border-gray-900 text-gray-900 px-6 py-3 rounded text-sm font-medium hover:bg-gray-900 hover:text-white transition-colors">
                Schedule a conversation →
              </button>
            </a>
          </div>
        </div>
      </section>

      {/* News & Updates */}
      <section className="py-16 md:py-20 px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-medium text-gray-900 mb-8 tracking-tight">News & Updates</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {pastNews.map((item, index) => {
              const CardWrapper = item.link ? 'a' : 'div';
              const cardProps = item.link ? { href: item.link, target: '_blank', rel: 'noopener noreferrer' } : {};
              
              return (
                <CardWrapper
                  key={index}
                  {...cardProps}
                  className="border border-gray-200 rounded-lg overflow-hidden hover:border-gray-300 hover:shadow-sm transition-all group"
                >
                  {item.image && (
                    <div className="bg-gray-50 p-6 flex items-center justify-center border-b border-gray-100">
                      <img src={item.image} alt={item.title} className="h-40 max-w-[70%] object-contain" />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-sm text-gray-500">{item.date}</span>
                      <span className={`text-xs font-medium px-2 py-1 rounded ${getCategoryColor(item.category)}`}>
                        {item.category}
                      </span>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2 group-hover:text-gray-700">{item.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{item.description}</p>
                    {item.link && (
                      <span className="inline-flex items-center text-sm font-medium text-gray-900 mt-3 group-hover:text-gray-700">
                        Learn more
                        <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </span>
                    )}
                  </div>
                </CardWrapper>
              );
            })}
          </div>
        </div>
      </section>

      {/* On Our Radar - Coming Soon Events */}
      <section className="py-16 md:py-20 px-8 bg-white border-t border-gray-100">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-medium text-gray-900 mb-2 tracking-tight">On Our Radar</h2>
          <p className="text-gray-600 mb-8">Conferences we're planning to attend — details coming soon</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {onRadarEvents.map((event, index) => (
              <a
                key={index}
                href={event.link}
                target="_blank"
                rel="noopener noreferrer"
                className="border border-gray-200 rounded-lg p-5 bg-gray-50 hover:border-gray-300 hover:bg-white transition-all group"
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-medium text-gray-500 bg-gray-200 px-2 py-1 rounded">{event.type}</span>
                </div>
                <h3 className="text-base font-medium text-gray-900 mb-2 group-hover:text-gray-700">{event.title}</h3>
                <div className="text-sm text-gray-500">
                  <p className="mb-1">{event.date}</p>
                  <p className="flex items-center">
                    <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    {event.location}
                  </p>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Signup */}
      <section className="py-16 md:py-20 px-8 bg-gray-50">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-medium text-gray-900 mb-4 tracking-tight">Stay informed</h2>
          <p className="text-gray-600 mb-8">
            Get updates on events, insights, and what we're learning about simulation and decision intelligence.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-md mx-auto">
            <input 
              type="email" 
              placeholder="your@email.com" 
              className="flex-1 px-4 py-3 border border-gray-300 rounded text-sm focus:outline-none focus:border-gray-400"
            />
            <button className="bg-gray-900 text-white px-6 py-3 rounded text-sm font-medium hover:bg-gray-800 transition-colors">
              Subscribe
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-4">No spam. Unsubscribe anytime.</p>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default NewsEventsPage;
